import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

const AlterarSenha = () => {
  // Estados para o formulário
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmaSenha, setConfirmaSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Estados para validação de senha
  const [requisitos, setRequisitos] = useState({
    comprimento: false,
    minuscula: false,
    maiuscula: false,
    numero: false,
    especial: false,
    iguais: false
  });
  
  // Obter dados do usuário do localStorage
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Verificar requisitos da senha quando o usuário digitar
  useEffect(() => {
    setRequisitos({
      comprimento: novaSenha.length >= 8,
      minuscula: /[a-z]/.test(novaSenha),
      maiuscula: /[A-Z]/.test(novaSenha),
      numero: /[0-9]/.test(novaSenha),
      especial: /[!@#$%&*]/.test(novaSenha),
      iguais: novaSenha === confirmaSenha && novaSenha !== ''
    });
  }, [novaSenha, confirmaSenha]);
  
  // Verificar se todos os requisitos foram atendidos
  const todosRequisitosAtendidos = () => {
    return Object.values(requisitos).every(req => req === true);
  };
  
  // Função para alternar visibilidade da senha
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  // Função para enviar a nova senha
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!todosRequisitosAtendidos()) {
      setError('Por favor, atenda a todos os requisitos de senha antes de continuar.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      const response = await axios({
        method: 'post',
        url: `${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api')}/usuarios/alterar-senha`,
        data: { nova_senha: novaSenha },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Resposta:', response.data);
      
      // Atualizar informações do usuário no localStorage
      const updatedUser = { ...userData, primeiro_acesso: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setSuccess(true);
      
      // Redirecionar após um breve delay
      setTimeout(() => {
        window.location.href = '/importacao';
      }, 2000);
      
    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      
      // Verificar se o erro contém detalhes sobre requisitos não atendidos
      if (err.response?.data?.requisitos) {
        // Atualizar os requisitos com base na resposta da API
        setRequisitos({
          ...requisitos,
          ...err.response.data.requisitos
        });
      }
      
      setError(err.response?.data?.message || 'Erro ao alterar senha. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          width: '100%',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <LockIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" component="h1" gutterBottom>
            Alteração de Senha Obrigatória
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Por motivos de segurança, você precisa criar uma nova senha.
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            Senha alterada com sucesso! Redirecionando...
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              label="Nova senha"
              type={showPassword ? 'text' : 'password'}
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <TextField
              label="Confirmar nova senha"
              type={showPassword ? 'text' : 'password'}
              value={confirmaSenha}
              onChange={(e) => setConfirmaSenha(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Requisitos de Senha:
            </Typography>
            
            <List dense>
              <ListItem>
                <ListItemIcon>
                  {requisitos.comprimento ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary="Mínimo de 8 caracteres" 
                  sx={{ 
                    color: requisitos.comprimento ? 'success.main' : 'error.main'
                  }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  {requisitos.minuscula ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary="Pelo menos uma letra minúscula" 
                  sx={{ 
                    color: requisitos.minuscula ? 'success.main' : 'error.main'
                  }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  {requisitos.maiuscula ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary="Pelo menos uma letra maiúscula" 
                  sx={{ 
                    color: requisitos.maiuscula ? 'success.main' : 'error.main'
                  }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  {requisitos.numero ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary="Pelo menos um número" 
                  sx={{ 
                    color: requisitos.numero ? 'success.main' : 'error.main'
                  }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  {requisitos.especial ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary="Pelo menos um caractere especial (!@#$%&*)" 
                  sx={{ 
                    color: requisitos.especial ? 'success.main' : 'error.main'
                  }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  {requisitos.iguais ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary="Senhas idênticas" 
                  sx={{ 
                    color: requisitos.iguais ? 'success.main' : 'error.main'
                  }}
                />
              </ListItem>
            </List>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={loading || !todosRequisitosAtendidos()}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Alterar Senha'}
            </Button>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default AlterarSenha; 